import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  trackAnalysisStartSuccess,
  trackAnalysisStartError,
  trackAnalysisClickSeeProgress,
} from '../../../../analytics';

import { FancyBtn, SellingPoint } from '../../../../components/custom';
import { Button, ProgressBar } from '../../../../components/general';

import { URL } from '../../../../constants';

import useSampleProjectData from '../../../../hooks/useSampleProjectData';

import { analyseAll } from '../../../../services/api/analyse';
import { useProjectStore } from '../../../../stores/project';

import { File, HlReel, AnalysisClips } from '../../../../types';
import {
  isEmpty,
  getUrl,
  getPluralPhrase,
  getSampleSelected,
} from '../../../../utils';

import { getAnalyseText } from '../../components/getText';

interface Props {
  peopleCount: number;
  clipsCount: number;
  hlReel: HlReel | null;
  clips: AnalysisClips | null;
  analyseStatus: string;
  onAnalysisStart: () => void;
  handleSampleAnalysisActions: (data: any, isAfter?: boolean) => void;
}

const AnalyseSubStep: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const queryParameters = new URLSearchParams(window.location.search);
  const startAnalysis = queryParameters.get('startAnalysis') === 'true';

  const {
    peopleCount,
    clipsCount,
    hlReel,
    clips,
    analyseStatus,
    onAnalysisStart,
    handleSampleAnalysisActions,
  } = props;

  const { updateCurrentEvent } = useProjectStore();

  const [isShowAnalyseBtn, setIsShowAnalyseBtn] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAnalysed = analyseStatus === 'analysed';

  // sample project data
  const { isSample, sampleProjectData } = useSampleProjectData(eventId);
  const [sampleStatus, setSampleStatus] = useState(
    isAnalysed ? analyseStatus : 'not-analysed',
  );
  const [sampleHlReel, setSampleHlReel] = useState(
    isAnalysed
      ? { ...sampleProjectData.hlReel, shots: [] }
      : (sampleProjectData.hlReel ?? {}),
  );

  const { sampleSelectedClips, sampleAnalysedClips } = useMemo(() => {
    const allSelectedClips = getSampleSelected(sampleProjectData.clips);

    const sampleSelectedClips =
      allSelectedClips.filter((clip: File) => !clip.isAnalysed) ?? [];
    const sampleAnalysedClips = allSelectedClips.filter(
      (clip: File) => clip.isAnalysed,
    );

    return { sampleSelectedClips, sampleAnalysedClips };
  }, [sampleProjectData.clips]);

  const [sampleClips, setSampleClips] = useState<AnalysisClips>({
    newClips: sampleStatus !== 'analysing' ? sampleSelectedClips : [],
    convertingClips: [],
    analysingClips: [],
    finishedClips: sampleAnalysedClips,
    errorClips: [],
  });

  // const intervalRef = useRef<ReturnType<typeof setInterval>>;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleSampleAnalysisProgress = useCallback(() => {
    setSampleClips((prevClips) => {
      const nextClips = { ...prevClips };

      // move from converting to analysing
      if (!isEmpty(nextClips.convertingClips)) {
        const convertingClip = nextClips.convertingClips.shift();
        if (convertingClip) nextClips.analysingClips.push(convertingClip);
      }

      // move from analysing to finished
      else if (!isEmpty(nextClips.analysingClips)) {
        const analysingClip = nextClips.analysingClips.shift();
        if (analysingClip) {
          analysingClip.isAnalysed = true;
          nextClips.finishedClips.push(analysingClip);
        }
      }

      // all clips are 'analysed'
      else {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // finish 'analysing' highlight reel
        setSampleHlReel({ ...sampleHlReel, shots: [] });

        setSampleStatus('analysed');
        sampleProjectData.isAssetsProcessed = true;
        // before updating clips in sampleProjectData – concat finishedClips with unselected clips to get all clips
        sampleProjectData.clips = nextClips.finishedClips.concat(
          getSampleSelected(sampleProjectData.clips, true),
        );

        handleSampleAnalysisActions(sampleProjectData, true);
      }

      return nextClips;
    });
  }, [sampleProjectData, sampleHlReel, handleSampleAnalysisActions]);

  const imitateAnalysing = useCallback(() => {
    setIsShowAnalyseBtn(false);
    setSampleStatus('analysing');
    sampleProjectData.event.stage = 'analysis';
    sampleProjectData.isAssetsProcessed = false;

    handleSampleAnalysisActions(sampleProjectData);

    setSampleClips((prev) => ({
      ...prev,
      newClips: [],
      convertingClips: sampleSelectedClips,
    }));

    intervalRef.current = setInterval(handleSampleAnalysisProgress, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [sampleSelectedClips, sampleProjectData]);

  useEffect(() => {
    if (startAnalysis) {
      onClickAnalyse();
      window.history.pushState(null, '', getUrl(URL.VIDEO_BUILDER, eventId));
    }
  }, [startAnalysis]);

  const onSuccessfulAnalysisStart = () => {
    trackAnalysisStartSuccess(startAnalysis, isSample);

    // remove eventId from localStorage arrays (for GA)
    ['analysedProjects', 'clipsWarningProjects'].forEach((key) => {
      const items = JSON.parse(localStorage.getItem(key) ?? '[]');
      localStorage.setItem(
        key,
        JSON.stringify(items.filter((id: string) => id !== eventId)),
      );
    });
  };

  const onClickAnalyse = async () => {
    setIsSubmitting(true);

    // sample project
    if (isSample) {
      // reset the clips & hl reel
      setSampleClips({
        ...sampleClips,
        convertingClips: sampleSelectedClips,
        finishedClips: [],
      });
      setSampleHlReel({ ...sampleProjectData.hlReel });

      setTimeout(() => {
        onSuccessfulAnalysisStart();
        imitateAnalysing();
        setIsSubmitting(false);
      }, 500);
    }

    // usual event
    else {
      onAnalysisStart();

      try {
        await analyseAll(eventId);
        await updateCurrentEvent();

        onSuccessfulAnalysisStart();
      } catch (err: any) {
        trackAnalysisStartError(err.message);
        toast.error(err.message);
      }

      setIsSubmitting(false);
    }
  };

  const { analyseText, progress } = getAnalyseText({
    hlReel: isSample ? sampleHlReel : hlReel,
    clips: isSample ? sampleClips : clips,
    peopleCount,
  });

  const status = isShowAnalyseBtn
    ? 'not-analysed'
    : isSample
      ? sampleStatus
      : analyseStatus;

  switch (status) {
    case 'not-analysed':
      return (
        <React.Fragment>
          <div>
            Analyse <b>{getPluralPhrase(clipsCount, 'clip')}</b> for{' '}
            <b>{getPluralPhrase(peopleCount, 'person')}</b>. Our AI will look
            for the best clips to add to your Highlight Reels wildcard shots.
          </div>

          <FancyBtn
            text="Analyse"
            onClick={onClickAnalyse}
            isSubmitting={isSubmitting}
          />
        </React.Fragment>
      );

    case 'analysing':
      return (
        <React.Fragment>
          <SellingPoint text={analyseText} />

          <div className="w-full flex flex-col gap-4">
            <ProgressBar progress={progress} />

            <div className="text-center">
              <div className="font-bold uppercase">Analysing</div>

              {!isSample && (
                <Link
                  to={getUrl(URL.VB_ANALYSIS, eventId)}
                  onClick={trackAnalysisClickSeeProgress}
                >
                  See progress
                </Link>
              )}
            </div>
          </div>
        </React.Fragment>
      );

    case 'analysed':
      return (
        <div>
          {analyseText}{' '}
          {!isSample && (
            <Button onClick={() => setIsShowAnalyseBtn(true)} variant="link">
              Analyse again
            </Button>
          )}
        </div>
      );
    default:
      return null;
  }
};

export default AnalyseSubStep;
